/**
 * GA4 Custom Event Tracking Implementation for Dutyman Blog
 *
 * This file contains all custom event tracking code for measuring blog content effectiveness
 * as outlined in the blog-measurement-strategy.md document.
 *
 * Installation: Add this code to your Shopify theme's main JavaScript file or create a
 * new snippet and include it in theme.liquid
 */

// ============================================================================
// TIER 2: ENGAGEMENT METRICS - Custom Event Tracking
// ============================================================================

/**
 * Event 1: Collection Link Click Tracking
 * Tracks when users click on collection links within blog articles
 * Measures how effectively blog content drives users to product collections
 */
function trackCollectionLinkClick() {
  document.addEventListener('DOMContentLoaded', function() {
    // Target all links within blog article content that point to collections
    const blogContent = document.querySelector('.blog-article, .article-content, [class*="blog"]');

    if (!blogContent) return; // Exit if not on a blog page

    const collectionLinks = blogContent.querySelectorAll('a[href*="/collections/"]');

    collectionLinks.forEach(function(link) {
      link.addEventListener('click', function(e) {
        const linkUrl = this.href;
        const sourcePage = window.location.pathname;
        const linkText = this.textContent.trim();

        // Find the paragraph position (which paragraph number contains this link)
        let linkPosition = 'unknown';
        const paragraphs = blogContent.querySelectorAll('p');
        paragraphs.forEach(function(p, index) {
          if (p.contains(link)) {
            linkPosition = 'paragraph-' + (index + 1);
          }
        });

        // Send event to GA4
        if (typeof gtag !== 'undefined') {
          gtag('event', 'collection_link_click', {
            'link_url': linkUrl,
            'source_page': sourcePage,
            'link_text': linkText,
            'link_position': linkPosition,
            'collection_name': linkUrl.split('/collections/')[1]?.split('?')[0]?.split('#')[0]
          });
        }

        console.log('Collection link click tracked:', {
          url: linkUrl,
          position: linkPosition,
          text: linkText
        });
      });
    });
  });
}

/**
 * Event 2: Scroll Depth Tracking
 * Tracks how far users scroll through blog articles
 * Measures content engagement and identifies drop-off points
 */
function trackScrollDepth() {
  // Only run on blog pages
  if (!window.location.pathname.includes('/blogs/')) return;

  const scrollMilestones = [25, 50, 75, 90, 100];
  const trackedMilestones = new Set();
  let startTime = Date.now();

  function calculateScrollPercentage() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
    return Math.min(scrollPercentage, 100);
  }

  function handleScroll() {
    const scrollPercentage = calculateScrollPercentage();

    scrollMilestones.forEach(function(milestone) {
      if (scrollPercentage >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone);

        const timeToDepth = Math.round((Date.now() - startTime) / 1000); // seconds

        if (typeof gtag !== 'undefined') {
          gtag('event', 'scroll_depth', {
            'page_path': window.location.pathname,
            'scroll_percentage': milestone,
            'time_to_depth': timeToDepth,
            'article_title': document.title
          });
        }

        console.log('Scroll depth tracked:', milestone + '% at ' + timeToDepth + 's');
      }
    });
  }

  // Throttle scroll events to avoid excessive firing
  let scrollTimeout;
  window.addEventListener('scroll', function() {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(handleScroll, 100);
  });
}

/**
 * Event 3: Article Read Completion
 * Tracks when users complete reading an article (reach bottom + spend minimum time)
 * Measures content quality and user engagement
 */
function trackArticleReadCompletion() {
  if (!window.location.pathname.includes('/blogs/')) return;

  let hasReachedBottom = false;
  let hasSpentMinimumTime = false;
  let completionTracked = false;
  const startTime = Date.now();
  const minimumReadTime = 60000; // 60 seconds minimum

  // Check if user reached bottom (95% scroll)
  function checkScrollCompletion() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

    if (scrollPercentage >= 95) {
      hasReachedBottom = true;
      checkCompletion();
    }
  }

  // Check if minimum time has elapsed
  function checkTimeCompletion() {
    if (Date.now() - startTime >= minimumReadTime) {
      hasSpentMinimumTime = true;
      checkCompletion();
    }
  }

  // Track completion when both conditions met
  function checkCompletion() {
    if (hasReachedBottom && hasSpentMinimumTime && !completionTracked) {
      completionTracked = true;

      const totalTimeSpent = Math.round((Date.now() - startTime) / 1000);
      const articleWordCount = countWords();

      if (typeof gtag !== 'undefined') {
        gtag('event', 'article_read_complete', {
          'page_path': window.location.pathname,
          'article_title': document.title,
          'time_spent': totalTimeSpent,
          'word_count': articleWordCount,
          'reading_speed': Math.round(articleWordCount / (totalTimeSpent / 60)) // words per minute
        });
      }

      console.log('Article read completion tracked:', {
        time: totalTimeSpent + 's',
        words: articleWordCount,
        speed: Math.round(articleWordCount / (totalTimeSpent / 60)) + ' wpm'
      });
    }
  }

  function countWords() {
    const articleContent = document.querySelector('.blog-article, .article-content, [class*="blog"]');
    if (!articleContent) return 0;
    const text = articleContent.textContent || articleContent.innerText;
    return text.trim().split(/\s+/).length;
  }

  // Event listeners
  window.addEventListener('scroll', checkScrollCompletion);
  setTimeout(checkTimeCompletion, minimumReadTime);
}

/**
 * Event 4: Blog-to-Collection Navigation
 * Enhanced tracking for users who navigate from blog to collection pages
 * Measures the effectiveness of content in driving product discovery
 */
function trackBlogToCollectionNavigation() {
  if (!window.location.pathname.includes('/blogs/')) return;

  // Store blog referral in sessionStorage
  sessionStorage.setItem('blog_referral', JSON.stringify({
    path: window.location.pathname,
    title: document.title,
    timestamp: Date.now()
  }));

  // Track when user navigates to collection from blog
  document.addEventListener('click', function(e) {
    const link = e.target.closest('a');
    if (link && link.href.includes('/collections/')) {
      const collectionUrl = link.href;
      const collectionName = collectionUrl.split('/collections/')[1]?.split('?')[0];

      if (typeof gtag !== 'undefined') {
        gtag('event', 'blog_to_collection', {
          'blog_article': window.location.pathname,
          'collection_url': collectionUrl,
          'collection_name': collectionName,
          'time_on_blog': Math.round((Date.now() - performance.timing.navigationStart) / 1000)
        });
      }
    }
  });
}

/**
 * Event 5: Collection Page from Blog Tracking
 * Tracks when users land on collection pages from blog articles
 * Install this on collection pages to measure blog referral effectiveness
 */
function trackCollectionFromBlog() {
  if (!window.location.pathname.includes('/collections/')) return;

  const blogReferral = sessionStorage.getItem('blog_referral');

  if (blogReferral) {
    try {
      const referralData = JSON.parse(blogReferral);
      const timeSinceReferral = Math.round((Date.now() - referralData.timestamp) / 1000);

      // Only track if navigation happened within last 5 minutes
      if (timeSinceReferral < 300) {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'collection_from_blog', {
            'blog_source': referralData.path,
            'blog_title': referralData.title,
            'collection_page': window.location.pathname,
            'time_since_blog': timeSinceReferral
          });
        }

        console.log('Collection page visited from blog:', referralData.path);
      }
    } catch (e) {
      console.error('Error parsing blog referral data:', e);
    }
  }
}

// ============================================================================
// TIER 3: CONVERSION METRICS - Enhanced Ecommerce Tracking
// ============================================================================

/**
 * Event 6: Blog-Assisted Add to Cart
 * Tracks when users add products to cart after visiting blog
 * Measures blog's influence on purchase intent
 */
function trackBlogAssistedAddToCart() {
  const blogReferral = sessionStorage.getItem('blog_referral');

  if (!blogReferral) return;

  // Listen for add to cart events (adjust selector based on your theme)
  document.addEventListener('click', function(e) {
    const addToCartButton = e.target.closest('[name="add"], .add-to-cart, [data-add-to-cart]');

    if (addToCartButton) {
      try {
        const referralData = JSON.parse(blogReferral);
        const timeSinceBlog = Math.round((Date.now() - referralData.timestamp) / 1000);

        if (timeSinceBlog < 1800) { // Within 30 minutes
          const productName = document.querySelector('.product-title, .product__title, h1')?.textContent.trim() || 'Unknown';

          if (typeof gtag !== 'undefined') {
            gtag('event', 'blog_assisted_add_to_cart', {
              'blog_source': referralData.path,
              'blog_title': referralData.title,
              'product_name': productName,
              'time_since_blog': timeSinceBlog,
              'currency': 'USD'
            });
          }

          console.log('Blog-assisted add to cart:', {
            blog: referralData.path,
            product: productName,
            time: timeSinceBlog + 's'
          });
        }
      } catch (e) {
        console.error('Error tracking blog-assisted add to cart:', e);
      }
    }
  });
}

/**
 * Event 7: Blog Session Tracking
 * Creates a user property to track users who engaged with blog content
 * Allows for blog visitor segmentation in GA4
 */
function setBlogVisitorProperty() {
  if (window.location.pathname.includes('/blogs/')) {
    if (typeof gtag !== 'undefined') {
      gtag('set', 'user_properties', {
        'blog_visitor': 'true',
        'last_blog_visit': new Date().toISOString().split('T')[0]
      });
    }

    // Store in localStorage for persistence across sessions
    localStorage.setItem('dutyman_blog_visitor', 'true');
    localStorage.setItem('dutyman_last_blog_visit', Date.now().toString());
  }
}

// ============================================================================
// TIER 4: SEO PERFORMANCE - Search Query Tracking
// ============================================================================

/**
 * Event 8: Track Organic Search Arrivals to Blog
 * Identifies which search queries bring users to blog articles
 * Helps measure SEO effectiveness
 */
function trackOrganicBlogArrival() {
  if (!window.location.pathname.includes('/blogs/')) return;

  const referrer = document.referrer;
  const searchEngines = {
    'google': /google\./,
    'bing': /bing\./,
    'yahoo': /yahoo\./,
    'duckduckgo': /duckduckgo\./
  };

  let searchEngine = null;
  for (const [engine, pattern] of Object.entries(searchEngines)) {
    if (pattern.test(referrer)) {
      searchEngine = engine;
      break;
    }
  }

  if (searchEngine) {
    // Try to extract search query from URL (may not always work due to privacy)
    let searchQuery = 'unknown';
    try {
      const referrerUrl = new URL(referrer);
      searchQuery = referrerUrl.searchParams.get('q') ||
                   referrerUrl.searchParams.get('p') ||
                   'unknown';
    } catch (e) {
      // Referrer parsing failed
    }

    if (typeof gtag !== 'undefined') {
      gtag('event', 'organic_blog_arrival', {
        'search_engine': searchEngine,
        'search_query': searchQuery,
        'landing_page': window.location.pathname,
        'article_title': document.title
      });
    }

    console.log('Organic arrival to blog:', {
      engine: searchEngine,
      query: searchQuery,
      page: window.location.pathname
    });
  }
}

// ============================================================================
// TIER 5: CONTENT QUALITY - Engagement Signals
// ============================================================================

/**
 * Event 9: Quick Exit Detection (Pogo-sticking)
 * Tracks when users quickly leave blog articles (< 10 seconds)
 * Indicates content quality issues or misleading titles
 */
function trackQuickExit() {
  if (!window.location.pathname.includes('/blogs/')) return;

  const arrivalTime = Date.now();
  let exitTracked = false;

  function handleExit() {
    if (exitTracked) return;

    const timeOnPage = Math.round((Date.now() - arrivalTime) / 1000);

    if (timeOnPage < 10) {
      exitTracked = true;

      if (typeof gtag !== 'undefined') {
        gtag('event', 'quick_exit', {
          'page_path': window.location.pathname,
          'article_title': document.title,
          'time_on_page': timeOnPage,
          'exit_type': 'pogo_stick'
        });
      }

      console.log('Quick exit detected:', timeOnPage + 's');
    }
  }

  // Track on beforeunload (page exit)
  window.addEventListener('beforeunload', handleExit);

  // Also track on visibility change (tab switch)
  document.addEventListener('visibilitychange', function() {
    if (document.hidden) {
      handleExit();
    }
  });
}

/**
 * Event 10: Related Article Click
 * Tracks when users click on related/recommended articles
 * Measures content interconnectivity and topic depth
 */
function trackRelatedArticleClick() {
  document.addEventListener('click', function(e) {
    const link = e.target.closest('a[href*="/blogs/"]');

    if (link && window.location.pathname.includes('/blogs/')) {
      const currentArticle = window.location.pathname;
      const targetArticle = link.href;

      // Only track if clicking to different article
      if (!targetArticle.includes(currentArticle)) {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'related_article_click', {
            'source_article': currentArticle,
            'target_article': targetArticle,
            'link_text': link.textContent.trim(),
            'link_position': link.closest('.related-posts, .recommended, [class*="related"]') ? 'related_section' : 'inline'
          });
        }

        console.log('Related article click:', {
          from: currentArticle,
          to: targetArticle
        });
      }
    }
  });
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize all tracking functions
 * Call this function when the page loads
 */
function initializeDutymanBlogTracking() {
  console.log('Initializing Dutyman blog tracking...');

  // Engagement tracking
  trackCollectionLinkClick();
  trackScrollDepth();
  trackArticleReadCompletion();
  trackBlogToCollectionNavigation();
  trackCollectionFromBlog();

  // Conversion tracking
  trackBlogAssistedAddToCart();
  setBlogVisitorProperty();

  // SEO tracking
  trackOrganicBlogArrival();

  // Quality signals
  trackQuickExit();
  trackRelatedArticleClick();

  console.log('Dutyman blog tracking initialized successfully');
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeDutymanBlogTracking);
} else {
  initializeDutymanBlogTracking();
}

// ============================================================================
// SHOPIFY INTEGRATION NOTES
// ============================================================================

/**
 * INSTALLATION INSTRUCTIONS:
 *
 * 1. In Shopify Admin, go to Online Store > Themes > Actions > Edit Code
 * 2. In the Assets folder, create a new file: ga4-blog-tracking.js
 * 3. Copy this entire file content into that new file
 * 4. In theme.liquid, add before </body>:
 *    <script src="{{ 'ga4-blog-tracking.js' | asset_url }}" defer></script>
 *
 * 5. Verify GA4 is already installed in your theme (check for gtag script)
 * 6. Test events in GA4 DebugView (requires GA4 Measurement ID in theme)
 *
 * TESTING IN GA4 DEBUGVIEW:
 * 1. Go to GA4 Property > Configure > DebugView
 * 2. Visit a blog page on your site
 * 3. Interact with content (scroll, click links, etc.)
 * 4. Verify events appear in DebugView
 *
 * CUSTOM DIMENSIONS TO CREATE IN GA4:
 * Go to GA4 Property > Configure > Custom Definitions > Create Custom Dimensions
 *
 * For collection_link_click event:
 * - Dimension: collection_name (Event parameter: collection_name)
 * - Dimension: link_position (Event parameter: link_position)
 * - Dimension: source_page (Event parameter: source_page)
 *
 * For scroll_depth event:
 * - Dimension: scroll_percentage (Event parameter: scroll_percentage)
 * - Dimension: time_to_depth (Event parameter: time_to_depth)
 *
 * For article_read_complete event:
 * - Dimension: reading_speed (Event parameter: reading_speed)
 * - Dimension: word_count (Event parameter: word_count)
 *
 * For blog_to_collection event:
 * - Dimension: blog_article (Event parameter: blog_article)
 * - Dimension: collection_name (Event parameter: collection_name)
 *
 * USER PROPERTIES TO CREATE:
 * Go to GA4 Property > Configure > Custom Definitions > Create Custom User Property
 * - Property: blog_visitor (User property: blog_visitor)
 * - Property: last_blog_visit (User property: last_blog_visit)
 */
